import { useState, useEffect, useCallback, ReactNode } from 'react';
import { EnvironmentPreferencesContext, type EnvironmentPreferences } from './useEnvironmentPreferences';
import { readEnvironmentPreferences } from './environmentPreferencesRead';

const RETRY_MS = [2_000, 5_000, 15_000, 30_000];

export const EnvironmentPreferencesProvider = ({ children }: { children: ReactNode }) => {
  const [environmentPreferences, setEnvironmentPreferences] = useState<EnvironmentPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => setAttempt(n => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const read = async (tries: number) => {
      try {
        const prefs = await readEnvironmentPreferences();
        if (cancelled) return;
        setEnvironmentPreferences(prefs);
        setFailed(false);
        setLoading(false);
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching env preferences:', error);
        setFailed(true);
        setLoading(false);
        if (tries < RETRY_MS.length) timer = setTimeout(() => void read(tries + 1), RETRY_MS[tries]);
      }
    };
    void read(0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [attempt]);

  // Retries that ran out pick up again when the person comes back to the tab or the network returns
  useEffect(() => {
    if (!failed) return;
    const onBack = () => {
      if (document.visibilityState === 'visible') retry();
    };
    window.addEventListener('online', retry);
    document.addEventListener('visibilitychange', onBack);
    return () => {
      window.removeEventListener('online', retry);
      document.removeEventListener('visibilitychange', onBack);
    };
  }, [failed, retry]);

  return (
    <EnvironmentPreferencesContext.Provider value={{ environmentPreferences, loading, failed, retry }}>
      {children}
    </EnvironmentPreferencesContext.Provider>
  );
};
